import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown } from "lucide-react";
import Checkbox from "shared/Checkbox/Checkbox";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { IFilterModal } from "Interfaces/interface.filter";
import { IFacetValue } from "Interfaces/interface.facet";
import ToTitleCase from "utils/ToTitleCase";
import { ICategory } from "@/Interfaces/interface.productsapi";

interface ExtendedFilterModalProps extends IFilterModal {
  selectedCategoryIds: string[];
  selectedSubCategoryIds: string[];
  selectedBrandIds: string[];
  onCategoryChange: (ids: string[]) => void;
  onSubCategoryChange: (ids: string[]) => void;
  onBrandChange: (ids: string[]) => void;
}

const FilterModal: React.FC<ExtendedFilterModalProps> = ({
  isOpen,
  onClose,
  facet,
  filterData,
  handleFilter,
  onOpenModal,
  selectedCategoryIds,
  selectedSubCategoryIds,
  selectedBrandIds,
  onCategoryChange,
  onSubCategoryChange,
  onBrandChange,
}) => {
  const [customMinPrice, setCustomMinPrice] = useState("");
  const [customMaxPrice, setCustomMaxPrice] = useState("");
  const [priceInputError, setPriceInputError] = useState("");
  const [tempSelections, setTempSelections] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  const toggleExpanded = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const handleMinPriceChange = (e: any) => {
    const value = e.target.value;

    if (value.length > 5) {
      setPriceInputError("Cannot enter more than 5 digits");
      return;
    } else {
      setPriceInputError("");
      setCustomMinPrice(value);
      if (value) {
        setTempSelections((prev) =>
          prev.filter((id) => !facet.values.some((val) => val.id === id))
        );
      }
    }
  };

  const handleMaxPriceChange = (e: any) => {
    const value = e.target.value;

    if (value.length > 5) {
      setPriceInputError("Cannot enter more than 5 digits");
      return;
    } else {
      setPriceInputError("");
      setCustomMaxPrice(value);
      if (value) {
        setTempSelections((prev) =>
          prev.filter((id) => !facet.values.some((val) => val.id === id))
        );
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      let currentFacetSelections: string[] = [];

      const lowerFacet = facet.name.toLowerCase();

      if (lowerFacet === "category") {
        currentFacetSelections = [
          ...selectedCategoryIds,
          ...selectedSubCategoryIds,
        ];
      } else if (lowerFacet === "brand") {
        currentFacetSelections = [...selectedBrandIds];
      } else if (lowerFacet === "price") {
        currentFacetSelections = filterData.filter(
          (id) =>
            facet.values.some((val) => val.id === id) ||
            (/^\d+-\d+$/.test(id) && !facet.values.some((val) => val.id === id))
        );
      } else {
        currentFacetSelections = filterData.filter((id) =>
          facet.values.some((val) => val.id === id)
        );
      }

      setTempSelections(currentFacetSelections);

      if (lowerFacet === "price") {
        setPriceInputError("");

        const customPriceRange = filterData.find(
          (id) =>
            /^\d+-\d+$/.test(id) && !facet.values.some((val) => val.id === id)
        );

        if (customPriceRange) {
          const [min, max] = customPriceRange.split("-");
          setCustomMinPrice(min);
          setCustomMaxPrice(max);
        } else {
          setCustomMinPrice("");
          setCustomMaxPrice("");
        }
      }

      setExpandedCategories(new Set());

      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    isOpen,
    filterData,
    facet.values,
    facet.name,
    selectedCategoryIds,
    selectedSubCategoryIds,
    selectedBrandIds,
  ]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  function isCategory(value: IFacetValue | ICategory): value is ICategory {
    return (value as ICategory).sub_category !== undefined;
  }

  const handleLocalSelection = useCallback(
    (value: IFacetValue | ICategory, isChecked: boolean) => {
      const lowerFacet = facet.name.toLowerCase();

      if (lowerFacet === "price") {
        if (isChecked) {
          setCustomMinPrice("");
          setCustomMaxPrice("");
          setPriceInputError("");
        }

        if (isChecked) {
          setTempSelections((prev) => [...new Set([...prev, value.id])]);
        } else {
          setTempSelections((prev) => prev.filter((id) => id !== value.id));
        }
        return;
      }

      if (lowerFacet === "category") {
        const allCategories = facet.values as ICategory[];

        const isSubCategory = allCategories.some((cat) =>
          cat.sub_category?.some((s) => s.id === value.id)
        );

        if (isSubCategory) {
          if (isChecked) {
            let newTemp = [...new Set([...tempSelections, value.id])];

            const parentCategory = allCategories.find((cat) =>
              cat.sub_category?.some((s) => s.id === value.id)
            );
            if (parentCategory) {
              newTemp = newTemp.filter((id) => id !== parentCategory.id);
            }

            setTempSelections(newTemp);
          } else {
            const newTemp = tempSelections.filter((id) => id !== value.id);
            setTempSelections(newTemp);
          }
        } else {
          if (isChecked) {
            let newTemp = [...new Set([...tempSelections, value.id])];

            const thisCat = allCategories.find((c) => c.id === value.id);
            if (thisCat) {
              const subsToRemove = thisCat.sub_category?.map((s) => s.id) || [];
              newTemp = newTemp.filter((id) => !subsToRemove.includes(id));
            }

            setTempSelections(newTemp);
          } else {
            let newTemp = tempSelections.filter((id) => id !== value.id);
            const thisCat = allCategories.find((c) => c.id === value.id);
            if (thisCat) {
              const subsToRemove = thisCat.sub_category?.map((s) => s.id) || [];
              newTemp = newTemp.filter((id) => !subsToRemove.includes(id));
            }
            setTempSelections(newTemp);
          }
        }
      } else if (lowerFacet === "brand") {
        if (isChecked) {
          setTempSelections((prev) => [...new Set([...prev, value.id])]);
        } else {
          setTempSelections((prev) => prev.filter((id) => id !== value.id));
        }
      } else {
        if (isChecked) {
          setTempSelections((prev) => [...new Set([...prev, value.id])]);
        } else {
          setTempSelections((prev) => prev.filter((id) => id !== value.id));
        }
      }
    },
    [facet.name, facet.values, tempSelections]
  );

  const handleCheckboxChange = (
    value: IFacetValue | ICategory,
    checked: boolean
  ) => {
    handleLocalSelection(value, checked);
  };

  if (!isOpen) return null;

  const isCustomPriceValid = () => {
    if (!customMinPrice || !customMaxPrice) return true;
    const min = parseFloat(customMinPrice);
    const max = parseFloat(customMaxPrice);
    return !isNaN(min) && !isNaN(max) && max > min;
  };

  const showPriceValidationError = () => {
    if (facet.name.toLowerCase() !== "price") return false;

    if (customMinPrice.trim() === "" && customMaxPrice.trim() === "")
      return false;

    return !isCustomPriceValid();
  };

  const isApplyDisabled = () => {
    const hasPriceError = priceInputError !== "";

    const min = Number(customMinPrice);
    const max = Number(customMaxPrice);
    const isCustomPriceInvalid = max <= min && (min > 0 || max > 0);

    if (facet.name.toLowerCase() === "price") {
      return hasPriceError || isCustomPriceInvalid;
    }

    return false;
  };

  const handleApply = () => {
    const lowerFacet = facet.name.toLowerCase();

    if (lowerFacet === "price") {
      const hasFacetCheckboxSelected = tempSelections.some((id) =>
        facet.values.some((val) => val.id === id)
      );

      if (hasFacetCheckboxSelected) {
        const existingCustomPrices = filterData.filter(
          (id) =>
            /^\d+-\d+$/.test(id) && !facet.values.some((val) => val.id === id)
        );

        existingCustomPrices.forEach((existingId) => {
          handleFilter(
            "Price",
            {
              id: existingId,
              name: `₹${existingId.replace("-", " - ₹")}`,
            },
            "remove"
          );
        });
      }

      tempSelections.forEach((id) => {
        const value = facet.values.find((val) => val.id === id);
        if (value) handleFilter("Price", value, "add");
      });

      if (
        !hasFacetCheckboxSelected &&
        customMinPrice &&
        customMaxPrice &&
        Number(customMaxPrice) > Number(customMinPrice)
      ) {
        const customId = `${customMinPrice}-${customMaxPrice}`;
        const customName = `₹${customMinPrice} - ₹${customMaxPrice}`;
        handleFilter("Price", { id: customId, name: customName }, "add");
      }

      onClose();
      return;
    }

    if (lowerFacet === "category") {
      const allCategories = facet.values as ICategory[];
      const newCategoryIds = tempSelections.filter((id) =>
        allCategories.some((c) => c.id === id)
      );
      const newSubCategoryIds = tempSelections.filter((id) =>
        allCategories.some((c) => c.sub_category?.some((s) => s.id === id))
      );

      onCategoryChange(newCategoryIds);
      onSubCategoryChange(newSubCategoryIds);

      onClose();
      return;
    }

    if (lowerFacet === "brand") {
      const newBrandIds = tempSelections;
      onBrandChange(newBrandIds);

      onClose();
      return;
    }

    const currentFacetSelections = filterData.filter((id) =>
      facet.values.some((val) => val.id === id)
    );

    currentFacetSelections.forEach((id) => {
      if (!tempSelections.includes(id)) {
        const value = facet.values.find((val) => val.id === id) || {
          id,
          name: id,
        };
        handleFilter(facet.name, value, "remove");
      }
    });

    tempSelections.forEach((id) => {
      if (!currentFacetSelections.includes(id)) {
        const value = facet.values.find((val) => val.id === id) || {
          id,
          name: id,
        };
        handleFilter(facet.name, value, "add");
      }
    });

    onClose();
  };

  const handleCancel = () => {
    const lowerFacet = facet.name.toLowerCase();

    if (lowerFacet === "category") {
      setTempSelections([...selectedCategoryIds, ...selectedSubCategoryIds]);
    } else if (lowerFacet === "brand") {
      setTempSelections([...selectedBrandIds]);
    } else {
      const currentFacetSelections = filterData.filter((id) =>
        facet.values.some((val) => val.id === id)
      );
      setTempSelections(currentFacetSelections);
    }

    setCustomMinPrice("");
    setCustomMaxPrice("");
    setPriceInputError("");
    onClose();
  };

  const preventNonNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const invalidChars = ["e", "E", "+", "-", ".", ","];
    if (invalidChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  const renderFacetOptions = () => {
    if (facet.name.toLowerCase() === "price") {
      return (
        <>
          {facet.values.map((val) => (
            <div key={val.id} className="flex items-center gap-3">
              <Checkbox
                checked={tempSelections.includes(val.id)}
                name={val.name}
                label={val.name}
                onChange={(checked) => handleCheckboxChange(val, checked)}
              />
            </div>
          ))}

          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600 font-medium">
              Custom Price Range:
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min ₹"
                onKeyDown={preventNonNumericInput}
                className={`border rounded px-3 py-2 text-sm w-1/2 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  priceInputError
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                value={customMinPrice}
                onChange={handleMinPriceChange}
              />
              <input
                type="number"
                placeholder="Max ₹"
                onKeyDown={preventNonNumericInput}
                className={`border rounded px-3 py-2 text-sm w-1/2 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  priceInputError
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                value={customMaxPrice}
                onChange={handleMaxPriceChange}
              />
            </div>

            {priceInputError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
                <p className="font-medium">⚠️ {priceInputError}</p>
              </div>
            )}
            {showPriceValidationError() && !priceInputError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-2 rounded">
                <p className="font-medium">
                  ⚠️ Max price must be greater than min price
                </p>
              </div>
            )}
            {customMinPrice &&
              customMaxPrice &&
              isCustomPriceValid() &&
              !priceInputError && (
                <div className="text-xs text-purple-600 bg-purple-50 p-2 rounded">
                  <p>
                    Custom price range: ₹{customMinPrice} - ₹{customMaxPrice}{" "}
                    will be applied when you click "Apply Filters"
                  </p>
                </div>
              )}
            {!customMinPrice &&
              !customMaxPrice &&
              tempSelections.some((id) => id.includes("-")) && (
                <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                  <p className="font-medium">Current selection:</p>
                  {tempSelections
                    .filter((id) => id.includes("-"))
                    .map((id) => (
                      <p key={id}>₹{id.replace("-", " - ₹")}</p>
                    ))}
                </div>
              )}
          </div>
        </>
      );
    } else if (facet.name.toLowerCase() === "category") {
      return (
        <>
          {facet.values.map((val: ICategory) => {
            const isExpanded = expandedCategories.has(val.id);
            const hasSubs = val.sub_category && val.sub_category.length > 0;

            return (
              <div key={val.id} className="mb-4">
                {/* Category Row */}
                <div className="flex items-center justify-between mb-2">
                  <Checkbox
                    checked={tempSelections.includes(val.id)}
                    name={val.name}
                    label={<ToTitleCase text={val.name} />}
                    onChange={(checked) => handleCheckboxChange(val, checked)}
                  />
                  {hasSubs && (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(val.id)}
                      className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors"
                      aria-expanded={isExpanded}
                      aria-label={`Toggle ${val.name} subcategories`}
                    >
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                </div>

                {isExpanded && hasSubs && (
                  <div className="ml-6 space-y-2 border-l border-gray-300 pl-4 py-2">
                    {val.sub_category.map((sub) => (
                      <div key={sub.id}>
                        <Checkbox
                          checked={tempSelections.includes(sub.id)}
                          name={sub.name}
                          label={<ToTitleCase text={sub.name} />}
                          onChange={(checked) =>
                            handleCheckboxChange(
                              { id: sub.id, name: sub.name },
                              checked
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                {!isExpanded && hasSubs && (
                  <span className="text-sm text-gray-500 ml-8 block">
                    {val.sub_category.length} sub category
                  </span>
                )}
              </div>
            );
          })}
        </>
      );
    } else {
      return facet.values.map((val) => (
        <div key={val.id} className="flex items-center gap-3">
          <Checkbox
            checked={tempSelections.includes(val.id)}
            name={val.name}
            label={<ToTitleCase text={val.name} />}
            onChange={(checked) => handleCheckboxChange(val, checked)}
          />
        </div>
      ));
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-end justify-center"
      onClick={handleCancel}
    >
      <div
        className="w-full max-w-md max-h-[85vh] rounded-t-2xl bg-white shadow-xl flex flex-col border border-gray-200 overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b px-6 py-4 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">
            {facet.name.toLowerCase() === "price" ? "₹ " : ""}
            <ToTitleCase text={facet.name} />
          </h2>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              Choose your preferred {facet.name.toLowerCase()}
            </p>
            <div className="space-y-3">{renderFacetOptions()}</div>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-white">
          <div className="flex gap-3">
            <ButtonSecondary
              variant="cancel"
              onClick={handleCancel}
              className="flex-1 border border-gray-300 py-3 rounded-xl text-sm font-medium"
            >
              Cancel
            </ButtonSecondary>

            <ButtonPrimary
              onClick={handleApply}
              disabled={isApplyDisabled()}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all `}
            >
              Apply Filters
            </ButtonPrimary>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FilterModal;
